import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import PatientPackagesModal from '../components/PatientPackagesModal';
import PatientMedicationsTab from '../components/PatientMedicationsTab';
import { SignatureTracker } from '../components/SignatureTracker';
import { PatientVisitNotes } from '../components/VisitNotes';
import { PatientFaxLog } from '../components/PatientFaxLog';
import { FaceSheetButton } from '../components/FaceSheetButton';
import { CaseloadModal } from '../components/CaseloadModal';
import { ChevronRight, User, Save, ArrowLeft, Plus, Info, Mail, Phone, MapPin, Calendar, Shield, Heart, Users, FileText, AlertCircle, FolderOpen, Pill } from 'lucide-react';
import api from '../api';
import { toast } from 'sonner';

const isoDate = (v) => { const t = String(v || '').split('T')[0]; const m = t.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/); return m ? `${m[3]}-${m[1].padStart(2, '0')}-${m[2].padStart(2, '0')}` : t; };

const PatientDemographicsDetailPage = () => {
  const { patientId: paramPatientId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Get patientId from either URL params or query string
  const patientId = paramPatientId || searchParams.get('patientId');
  useEffect(() => { if (patientId) localStorage.setItem('ivory_current_patient', patientId); }, [patientId]);
  
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'demographics');
  const [showPackagesModal, setShowPackagesModal] = useState(false);
  const [showCaseload, setShowCaseload] = useState(false);

  // Demographics Form State
  const [formData, setFormData] = useState({
    // Personal Information
    firstName: '',
    lastName: '',
    middleName: '',
    suffix: '',
    maidenName: '',
    preferredName: '',
    dateOfBirth: '',
    ssn: '',
    gender: '',
    maritalStatus: '',
    race: '',
    ethnicity: '',
    primaryLanguage: 'English',
    religion: '',
    
    // Contact Information
    address: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
    county: '',
    phone: '',
    altPhone: '',
    cellPhone: '',
    email: '',
    
    // Emergency Contact
    emergencyContactName: '',
    emergencyContactRelation: '',
    emergencyContactPhone: '',
    emergencyContactAltPhone: '',
    emergencyContactAddress: '',
    
    // Additional Information
    medicareNumber: '',
    medicaidNumber: '',
    directives: '',
    notes: ''
  });

  const tabs = [
    { id: 'demographics', label: 'Demographics' },
    { id: 'medications', label: 'Medications' },
    { id: 'signatures', label: '485 Signatures' },
    { id: 'visitNotes', label: 'Visit Notes' },
    { id: 'faxes', label: 'Faxes' },
    { id: 'insurance', label: 'Insurance' },
    { id: 'contacts', label: 'Contacts' },
    { id: 'emergencyContacts', label: 'Emergency Contacts' },
    { id: 'referrals', label: 'Referrals' },
    { id: 'other', label: 'Other' }
  ];

  const genderOptions = ['Male', 'Female', 'Other', 'Unknown'];
  const maritalStatusOptions = ['Single', 'Married', 'Divorced', 'Widowed', 'Separated', 'Unknown'];
  const raceOptions = ['American Indian or Alaska Native', 'Asian', 'Black or African American', 'Native Hawaiian or Other Pacific Islander', 'White', 'Two or More Races', 'Unknown'];
  const ethnicityOptions = ['Hispanic or Latino', 'Not Hispanic or Latino', 'Unknown'];
  const stateOptions = ['AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'];
  const relationOptions = ['Spouse', 'Parent', 'Child', 'Sibling', 'Friend', 'Other'];

  useEffect(() => {
    if (patientId) {
      fetchPatient();
    } else {
      setLoading(false);
    }
  }, [patientId]);

  const fetchPatient = async () => {
    try {
      const response = await api.get(`/patients/${patientId}`);
      if (response.data.success) {
        const p = response.data.patient;
        setPatient(p);
        setFormData({
          firstName: p.firstName || '',
          lastName: p.lastName || '',
          middleName: p.middleName || '',
          suffix: p.suffix || '',
          maidenName: p.maidenName || '',
          preferredName: p.preferredName || '',
          dateOfBirth: isoDate(p.dateOfBirth),
          ssn: p.ssn || '',
          gender: p.gender || '',
          maritalStatus: p.maritalStatus || '',
          race: p.race || '',
          ethnicity: p.ethnicity || '',
          primaryLanguage: p.primaryLanguage || 'English',
          religion: p.religion || '',
          address: p.address || '',
          address2: p.address2 || '',
          city: p.city || '',
          state: p.state || '',
          zip: p.zipCode || p.zip || '',
          county: p.county || '',
          phone: p.phone || '',
          altPhone: p.altPhone || '',
          cellPhone: p.cellPhone || '',
          email: p.email || '',
          emergencyContactName: p.emergencyContactName || '',
          emergencyContactRelation: p.emergencyContactRelation || '',
          emergencyContactPhone: p.emergencyContactPhone || '',
          emergencyContactAltPhone: p.emergencyContactAltPhone || '',
          emergencyContactAddress: p.emergencyContactAddress || '',
          medicareNumber: p.medicareNumber || '',
          medicaidNumber: p.medicaidNumber || '',
          directives: p.directives || '',
          notes: p.notes || ''
        });
      }
    } catch (error) {
      toast.error('Failed to load patient');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await api.put(`/patients/${patientId}`, { ...formData, zipCode: formData.zip });
      if (response.data.success) {
        toast.success('Patient demographics saved successfully');
        setPatient({ ...patient, ...formData });
      }
    } catch (error) {
      toast.error('Failed to save patient demographics');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    if (dateStr.includes('/')) return dateStr;
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  };

  const ownsChart = !patient?.userId || patient.userId === JSON.parse(localStorage.getItem('ivory_user') || '{}').id;
  const quickLinks = [
    ...(ownsChart ? [{ label: 'Assign Nurses', icon: Users, onClick: () => setShowCaseload(true) }] : []),
    { label: 'Application Packages', icon: FolderOpen, onClick: () => setShowPackagesModal(true) },
    { label: 'COVID-19 Patient Screening', icon: AlertCircle },
    { label: '485', icon: FileText },
    { label: 'Aide Docs', icon: FileText },
    { label: 'Braden Scale', icon: FileText },
    { label: 'Ivory Mail', icon: Mail },
    { label: 'Care Team', icon: Users },
    { label: 'Claims', icon: FileText },
    { label: 'Communication Log', icon: FileText },
    { label: 'Electronic Health Records', icon: FileText },
    { label: 'Invoice', icon: FileText },
    { label: 'Med Profile', icon: Heart },
    { label: 'Missed Visit', icon: Calendar },
    { label: 'MSW', icon: FileText },
    { label: 'Assessments (OASIS Mgmt)', icon: FileText },
    { label: 'Admission', icon: FileText, onClick: () => navigate(`/dashboard/patients/admission/${patientId}`) }
  ];

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Loading...</div>
        </div>
      </DashboardLayout>
    );
  }

  if (!patient) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500 text-center">
            <p>Patient not found</p>
            <button onClick={() => navigate('/dashboard/patients/demographics')} className="mt-3 px-4 py-2 rounded bg-[#5bc0de] text-white" data-testid="not-found-patient-list">Go to patient list</button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="flex" data-testid="patient-demographics-detail">
        {/* Main Content */}
        <div className="flex-1 pr-4">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 mb-4 text-sm">
            <button onClick={() => navigate('/dashboard/patients/demographics')} className="text-[#5bc0de] text-xl font-light hover:underline" data-testid="breadcrumb-patient-list">Patient Demographics</button>
            <ChevronRight className="w-4 h-4 text-gray-400" />
            <span className="text-gray-500">Edit Patient</span>
          </div>

          {/* Tabs - Alora Plus Style */}
          <div className="flex border-b border-gray-200 mb-4">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`tab-${tab.id}`}
                className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                  activeTab === tab.id
                    ? 'border-[#5bc0de] text-[#5bc0de] bg-white'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Patient Header */}
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-semibold text-gray-800" data-testid="patient-name">
                {patient.lastName}, {patient.firstName} {patient.middleName || ''}
              </h1>
              <div className="flex items-center gap-4 mt-1">
                <span className="text-gray-500 text-sm">
                  <span className="font-medium">DOB:</span> {formatDate(patient.dateOfBirth)}
                </span>
                <span className="text-gray-500 text-sm">
                  <span className="font-medium">MR#:</span> <span data-testid="patient-mrn">{patient.medicalRecordNumber || 'Assigning…'}</span>
                  {patient.pan ? <span className="ml-4"><span className="font-medium">PAN:</span> {patient.pan}</span> : null}
                </span>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <FaceSheetButton patient={patient} />
              <button
                onClick={() => navigate('/dashboard/patients/demographics')}
                className="px-4 py-2 border border-gray-300 rounded text-gray-600 hover:bg-gray-50 flex items-center gap-2"
                data-testid="all-patients-btn"
              >
                <Users className="w-4 h-4" />
                All Patients
              </button>
              <button
                onClick={() => navigate(-1)}
                className="px-4 py-2 border border-gray-300 rounded text-gray-600 hover:bg-gray-50 flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>
              {!['visitNotes', 'faxes'].includes(activeTab) && <button
                onClick={handleSave}
                disabled={saving}
                data-testid="save-demographics-btn"
                className="px-4 py-2 bg-[#5bc0de] text-white rounded hover:bg-[#46b8da] flex items-center gap-2 disabled:opacity-50"
              >
                <Save className="w-4 h-4" />
                {saving ? 'Saving...' : 'Save'}
              </button>}
            </div>
          </div>

          {/* Demographics Tab Content */}
          {activeTab === 'demographics' && (
            <div className="space-y-6">
              {/* Personal Information Section */}
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-[#5bc0de] text-white px-4 py-2 font-medium flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Personal Information
                </div>
                <div className="p-4 grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                    <input
                      type="text"
                      value={formData.lastName}
                      onChange={(e) => handleInputChange('lastName', e.target.value)}
                      className="w-full border border-gray-300 rounded px-3 py-1.5 focus:outline-none focus:border-[#5bc0de]"
                      data-testid="input-lastName"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                    <input
                      type="text"
                      value={formData.firstName}
                      onChange={(e) => handleInputChange('firstName', e.target.value)}
                      className="w-full border border-gray-300 rounded px-3 py-1.5 focus:outline-none focus:border-[#5bc0de]"
                      data-testid="input-firstName"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Middle Name</label>
                    <input
                      type="text"
                      value={formData.middleName}
                      onChange={(e) => handleInputChange('middleName', e.target.value)}
                      className="w-full border border-gray-300 rounded px-3 py-1.5 focus:outline-none focus:border-[#5bc0de]"
                      data-testid="input-middleName"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Suffix</label>
                    <input
                      type="text"
                      value={formData.suffix}
                      onChange={(e) => handleInputChange('suffix', e.target.value)}
                      placeholder="Jr., Sr., III, etc."
                      className="w-full border border-gray-300 rounded px-3 py-1.5 focus:outline-none focus:border-[#5bc0de]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Maiden Name</label>
                    <input
                      type="text"
                      value={formData.maidenName}
                      onChange={(e) => handleInputChange('maidenName', e.target.value)}
                      className="w-full border border-gray-300 rounded px-3 py-1.5 focus:outline-none focus:border-[#5bc0de]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Preferred Name</label>
                    <input
                      type="text"
                      value={formData.preferredName}
                      onChange={(e) => handleInputChange('preferredName', e.target.value)}
                      className="w-full border border-gray-300 rounded px-3 py-1.5 focus:outline-none focus:border-[#5bc0de]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth *</label>
                    <input
                      type="date"
                      value={formData.dateOfBirth}
                      onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                      className="w-full border border-gray-300 rounded px-3 py-1.5 focus:outline-none focus:border-[#5bc0de]"
                      data-testid="input-dateOfBirth"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">SSN</label>
                    <input
                      type="text"
                      value={formData.ssn}
                      onChange={(e) => handleInputChange('ssn', e.target.value)}
                      placeholder="XXX-XX-XXXX"
                      data-testid="input-ssn"
                      className="w-full border border-gray-300 rounded px-3 py-1.5 focus:outline-none focus:border-[#5bc0de]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Gender *</label>
                    <select
                      value={formData.gender}
                      onChange={(e) => handleInputChange('gender', e.target.value)}
                      className="w-full border border-gray-300 rounded px-3 py-1.5 focus:outline-none focus:border-[#5bc0de]"
                      data-testid="input-gender"
                    >
                      <option value="">Select Gender</option>
                      {genderOptions.map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Marital Status</label>
                    <select
                      value={formData.maritalStatus}
                      onChange={(e) => handleInputChange('maritalStatus', e.target.value)}
                      data-testid="input-maritalStatus"
                      className="w-full border border-gray-300 rounded px-3 py-1.5 focus:outline-none focus:border-[#5bc0de]"
                    >
                      <option value="">Select Status</option>
                      {maritalStatusOptions.map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Race</label>
                    <select
                      value={formData.race}
                      onChange={(e) => handleInputChange('race', e.target.value)}
                      data-testid="input-race"
                      className="w-full border border-gray-300 rounded px-3 py-1.5 focus:outline-none focus:border-[#5bc0de]"
                    >
                      <option value="">Select Race</option>
                      {raceOptions.map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Ethnicity</label>
                    <select
                      value={formData.ethnicity}
                      onChange={(e) => handleInputChange('ethnicity', e.target.value)}
                      data-testid="input-ethnicity"
                      className="w-full border border-gray-300 rounded px-3 py-1.5 focus:outline-none focus:border-[#5bc0de]"
                    >
                      <option value="">Select Ethnicity</option>
                      {ethnicityOptions.map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Primary Language</label>
                    <input
                      type="text"
                      value={formData.primaryLanguage}
                      onChange={(e) => handleInputChange('primaryLanguage', e.target.value)}
                      className="w-full border border-gray-300 rounded px-3 py-1.5 focus:outline-none focus:border-[#5bc0de]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Religion</label>
                    <input
                      type="text"
                      value={formData.religion}
                      onChange={(e) => handleInputChange('religion', e.target.value)}
                      className="w-full border border-gray-300 rounded px-3 py-1.5 focus:outline-none focus:border-[#5bc0de]"
                    />
                  </div>
                </div>
              </div>

              {/* Contact Information Section */}
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-[#5bc0de] text-white px-4 py-2 font-medium flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Contact Information
                </div>
                <div className="p-4 grid grid-cols-3 gap-4">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Address *</label>
                    <input
                      type="text"
                      value={formData.address}
                      onChange={(e) => handleInputChange('address', e.target.value)}
                      className="w-full border border-gray-300 rounded px-3 py-1.5 focus:outline-none focus:border-[#5bc0de]"
                      data-testid="input-address"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Address 2</label>
                    <input
                      type="text"
                      value={formData.address2}
                      onChange={(e) => handleInputChange('address2', e.target.value)}
                      placeholder="Apt, Suite, Unit, etc."
                      className="w-full border border-gray-300 rounded px-3 py-1.5 focus:outline-none focus:border-[#5bc0de]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">City *</label>
                    <input
                      type="text"
                      value={formData.city}
                      onChange={(e) => handleInputChange('city', e.target.value)}
                      className="w-full border border-gray-300 rounded px-3 py-1.5 focus:outline-none focus:border-[#5bc0de]"
                      data-testid="input-city"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">State *</label>
                    <select
                      value={formData.state}
                      onChange={(e) => handleInputChange('state', e.target.value)}
                      className="w-full border border-gray-300 rounded px-3 py-1.5 focus:outline-none focus:border-[#5bc0de]"
                      data-testid="input-state"
                    >
                      <option value="">Select State</option>
                      {stateOptions.map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">ZIP Code *</label>
                    <input
                      type="text"
                      value={formData.zip}
                      onChange={(e) => handleInputChange('zip', e.target.value)}
                      className="w-full border border-gray-300 rounded px-3 py-1.5 focus:outline-none focus:border-[#5bc0de]"
                      data-testid="input-zip"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">County</label>
                    <input
                      type="text"
                      value={formData.county}
                      onChange={(e) => handleInputChange('county', e.target.value)}
                      className="w-full border border-gray-300 rounded px-3 py-1.5 focus:outline-none focus:border-[#5bc0de]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Primary Phone *</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      placeholder="(XXX) XXX-XXXX"
                      className="w-full border border-gray-300 rounded px-3 py-1.5 focus:outline-none focus:border-[#5bc0de]"
                      data-testid="input-phone"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Alternate Phone</label>
                    <input
                      type="tel"
                      value={formData.altPhone}
                      onChange={(e) => handleInputChange('altPhone', e.target.value)}
                      placeholder="(XXX) XXX-XXXX"
                      className="w-full border border-gray-300 rounded px-3 py-1.5 focus:outline-none focus:border-[#5bc0de]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Cell Phone</label>
                    <input
                      type="tel"
                      value={formData.cellPhone}
                      onChange={(e) => handleInputChange('cellPhone', e.target.value)}
                      placeholder="(XXX) XXX-XXXX"
                      className="w-full border border-gray-300 rounded px-3 py-1.5 focus:outline-none focus:border-[#5bc0de]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      className="w-full border border-gray-300 rounded px-3 py-1.5 focus:outline-none focus:border-[#5bc0de]"
                      data-testid="input-email"
                    />
                  </div>
                </div>
              </div>

              {/* Medicare/Medicaid Section */}
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-[#5bc0de] text-white px-4 py-2 font-medium flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  Medicare/Medicaid Information
                </div>
                <div className="p-4 grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Medicare Number</label>
                    <input
                      type="text"
                      value={formData.medicareNumber}
                      onChange={(e) => handleInputChange('medicareNumber', e.target.value)}
                      className="w-full border border-gray-300 rounded px-3 py-1.5 focus:outline-none focus:border-[#5bc0de]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Medicaid Number</label>
                    <input
                      type="text"
                      value={formData.medicaidNumber}
                      onChange={(e) => handleInputChange('medicaidNumber', e.target.value)}
                      className="w-full border border-gray-300 rounded px-3 py-1.5 focus:outline-none focus:border-[#5bc0de]"
                    />
                  </div>
                </div>
              </div>

              {/* Additional Notes Section */}
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-[#5bc0de] text-white px-4 py-2 font-medium flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Additional Information
                </div>
                <div className="p-4 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Advance Directives</label>
                    <textarea
                      value={formData.directives}
                      onChange={(e) => handleInputChange('directives', e.target.value)}
                      rows={3}
                      className="w-full border border-gray-300 rounded px-3 py-1.5 focus:outline-none focus:border-[#5bc0de]"
                      placeholder="Enter any advance directives..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => handleInputChange('notes', e.target.value)}
                      rows={3}
                      className="w-full border border-gray-300 rounded px-3 py-1.5 focus:outline-none focus:border-[#5bc0de]"
                      placeholder="Enter any additional notes..."
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Medications Tab Content */}
          {activeTab === 'medications' && (
            <PatientMedicationsTab 
              patientId={patientId} 
              patientName={`${patient?.firstName || ''} ${patient?.lastName || ''}`}
            />
          )}

          {activeTab === 'signatures' && <SignatureTracker patientId={patientId} />}
          {activeTab === 'visitNotes' && <PatientVisitNotes patientId={patientId} />}
          {activeTab === 'faxes' && <PatientFaxLog patientId={patientId} />}

          {/* Insurance Tab Content */}
          {activeTab === 'insurance' && (
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-[#5bc0de] text-white px-4 py-2 font-medium flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  Insurance Information
                </div>
                <button className="bg-white text-[#5bc0de] px-3 py-1 rounded text-sm flex items-center gap-1">
                  <Plus className="w-4 h-4" />
                  Add Insurance
                </button>
              </div>
              <div className="p-4">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b">
                      <th className="px-4 py-2 text-left text-gray-600">Actions</th>
                      <th className="px-4 py-2 text-left text-gray-600">Responsibility</th>
                      <th className="px-4 py-2 text-left text-gray-600">Payer</th>
                      <th className="px-4 py-2 text-left text-gray-600">Policy #</th>
                      <th className="px-4 py-2 text-left text-gray-600">Group #</th>
                      <th className="px-4 py-2 text-left text-gray-600">Effective From</th>
                      <th className="px-4 py-2 text-left text-gray-600">Effective Thru</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <button className="text-[#5bc0de] hover:text-[#46b8da]">
                          Edit
                        </button>
                      </td>
                      <td className="px-4 py-3">Primary</td>
                      <td className="px-4 py-3">{patient.insurance || patient.insurance_provider || 'CIGNA'}</td>
                      <td className="px-4 py-3">{patient.policy_id || 'N/A'}</td>
                      <td className="px-4 py-3">{patient.group_number || 'N/A'}</td>
                      <td className="px-4 py-3">{formatDate(patient.insuranceEffectiveDate) || 'N/A'}</td>
                      <td className="px-4 py-3">{formatDate(patient.insuranceTerminationDate) || 'N/A'}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Contacts Tab Content */}
          {activeTab === 'contacts' && (
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-[#5bc0de] text-white px-4 py-2 font-medium flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Contacts
                </div>
                <button className="bg-white text-[#5bc0de] px-3 py-1 rounded text-sm flex items-center gap-1">
                  <Plus className="w-4 h-4" />
                  Add Contact
                </button>
              </div>
              <div className="p-4">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b">
                      <th className="px-4 py-2 text-left text-gray-600">Actions</th>
                      <th className="px-4 py-2 text-left text-gray-600">Name</th>
                      <th className="px-4 py-2 text-left text-gray-600">Relationship</th>
                      <th className="px-4 py-2 text-left text-gray-600">Phone</th>
                      <th className="px-4 py-2 text-left text-gray-600">Email</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td colSpan="5" className="px-4 py-8 text-center text-gray-500">
                        No contacts added yet
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Emergency Contacts Tab Content */}
          {activeTab === 'emergencyContacts' && (
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-[#5bc0de] text-white px-4 py-2 font-medium flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                Emergency Contact Information
              </div>
              <div className="p-4 grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Emergency Contact Name *</label>
                  <input
                    type="text"
                    value={formData.emergencyContactName}
                    onChange={(e) => handleInputChange('emergencyContactName', e.target.value)}
                    className="w-full border border-gray-300 rounded px-3 py-1.5 focus:outline-none focus:border-[#5bc0de]"
                    data-testid="input-emergencyContactName"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Relationship *</label>
                  <select
                    value={formData.emergencyContactRelation}
                    onChange={(e) => handleInputChange('emergencyContactRelation', e.target.value)}
                    data-testid="input-emergencyContactRelation"
                    className="w-full border border-gray-300 rounded px-3 py-1.5 focus:outline-none focus:border-[#5bc0de]"
                  >
                    <option value="">Select Relationship</option>
                    {relationOptions.map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Primary Phone *</label>
                  <input
                    type="tel"
                    value={formData.emergencyContactPhone}
                    onChange={(e) => handleInputChange('emergencyContactPhone', e.target.value)}
                    placeholder="(XXX) XXX-XXXX"
                    className="w-full border border-gray-300 rounded px-3 py-1.5 focus:outline-none focus:border-[#5bc0de]"
                    data-testid="input-emergencyContactPhone"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Alternate Phone</label>
                  <input
                    type="tel"
                    value={formData.emergencyContactAltPhone}
                    onChange={(e) => handleInputChange('emergencyContactAltPhone', e.target.value)}
                    placeholder="(XXX) XXX-XXXX"
                    className="w-full border border-gray-300 rounded px-3 py-1.5 focus:outline-none focus:border-[#5bc0de]"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                  <input
                    type="text"
                    value={formData.emergencyContactAddress}
                    onChange={(e) => handleInputChange('emergencyContactAddress', e.target.value)}
                    className="w-full border border-gray-300 rounded px-3 py-1.5 focus:outline-none focus:border-[#5bc0de]"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Referrals Tab Content */}
          {activeTab === 'referrals' && (
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-[#5bc0de] text-white px-4 py-2 font-medium flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Referral Information
                </div>
                <button className="bg-white text-[#5bc0de] px-3 py-1 rounded text-sm flex items-center gap-1">
                  <Plus className="w-4 h-4" />
                  Add Referral
                </button>
              </div>
              <div className="p-4">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b">
                      <th className="px-4 py-2 text-left text-gray-600">Actions</th>
                      <th className="px-4 py-2 text-left text-gray-600">Referral Source</th>
                      <th className="px-4 py-2 text-left text-gray-600">Contact Name</th>
                      <th className="px-4 py-2 text-left text-gray-600">Phone</th>
                      <th className="px-4 py-2 text-left text-gray-600">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td colSpan="5" className="px-4 py-8 text-center text-gray-500">
                        No referrals added yet
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Other Tab Content */}
          {activeTab === 'other' && (
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-[#5bc0de] text-white px-4 py-2 font-medium flex items-center gap-2">
                <Info className="w-4 h-4" />
                Other Information
              </div>
              <div className="p-4">
                <p className="text-gray-500 text-center py-8">Additional patient information can be added here.</p>
              </div>
            </div>
          )}

          {/* Bottom Action Buttons */}
          {!['visitNotes', 'faxes'].includes(activeTab) && <div className="flex justify-between mt-6">
            <button
              onClick={() => navigate(-1)}
              className="px-4 py-2 border border-gray-300 rounded text-gray-600 hover:bg-gray-50"
            >
              Cancel
            </button>
            <div className="flex gap-2">
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-6 py-2 bg-[#5bc0de] text-white rounded hover:bg-[#46b8da] disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>}
        </div>

        {/* Right Sidebar - Quick Links */}
        <div className="w-64 border-l border-gray-200 pl-4">
          <div className="text-sm font-medium text-gray-700 mb-3">Go To</div>
          <div className="space-y-1">
            {quickLinks.map((link, idx) => {
              const IconComponent = link.icon;
              return (
                <button
                  key={idx}
                  onClick={link.onClick}
                  data-testid={`quick-link-${link.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded text-left"
                >
                  <IconComponent className="w-4 h-4 text-[#5bc0de]" />
                  {link.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {showCaseload && <CaseloadModal patientId={patientId} patientName={`${patient?.firstName || ''} ${patient?.lastName || ''}`} onClose={() => setShowCaseload(false)} />}
      {/* Patient Packages Modal */}
      <PatientPackagesModal
        patient={patient}
        isOpen={showPackagesModal}
        onClose={() => setShowPackagesModal(false)}
      />
    </DashboardLayout>
  );
};

export default PatientDemographicsDetailPage;
